import React, { Fragment, useState } from "react";
import "./index.less";
import { AvatarCom, AvatarComProps } from "../AvatarCom";
import Icon from "@ant-design/icons";
import { Menu, Popover, Skeleton } from "antd";
import { useTranslate } from "@obsbot/remo-i18n";
import { useGlobalStore } from "@obsbot/remo-stores";
import { changeUserStatus } from "@obsbot/remo-server-api";
import { eventBus } from "@obsbot/remo-utils";

export interface PersonalInfoProps {
    showSetting?: boolean;
    avatarInfo: AvatarComProps;
    handleQuitAccount?: () => void;
    handlePersonalSetting?: () => void;
}

export const PersonalCard: React.FC<PersonalInfoProps> = ({
    avatarInfo = {
        avatar: "https://www.figma.com/design/GHyIdj2sYIAmBs4tH73omC/%E6%B8%B8%E6%88%8F%E5%BC%80%E9%BB%91?node-id=544-1395&t=SpWzBPqHhRADvN6e-4",
        userID: "123",
        userName: "test",
        status: "offline",
        size: 50,
    },
    showSetting = true,
    handleQuitAccount,
    handlePersonalSetting,
}) => {
    const t = useTranslate();
    const { updateOnlineStatus } = useGlobalStore();
    const [selectedKey, setSelectedKey] = useState<string>(avatarInfo.status ?? "offline");
    const statusMenu = [
        {
            label: (
                <div>
                    <div className="dot dot-online"></div>
                    {t("account.status.online")}
                </div>
            ),
            key: "online",
        },
        {
            label: (
                <div>
                    <div className="dot dot-invisible"></div>
                    {t("account.status.invisible")}
                </div>
            ),
            key: "invisible",
        },
    ];

    const changeStatus = async ({ key }: { key: string }): Promise<void> => {
        setSelectedKey(key);
        const res = await changeUserStatus(key);
        if (res.code === "200") {
            updateOnlineStatus(key);
        } else {
            eventBus.emit("show-message", {
                message: t("account.change-status-failed-tips"),
                type: "error",
            });
        }
    };
    return (
        <div className="personal-card">
            <div className="header-bg">
                <Logo />
            </div>
            <div className="avatar-container">
                {avatarInfo.avatar === "loading" ? (
                    <Skeleton.Avatar active={true} shape="circle" size={56} />
                ) : (
                    <AvatarCom
                        avatar={avatarInfo.avatar}
                        size={56}
                        status=""
                        userID={avatarInfo.userID}
                    />
                )}
                <div className="user-info">
                    <span className="user-name">{avatarInfo.userName}</span>
                </div>
            </div>
            {showSetting && (
                <Fragment>
                    <div className="account-fns">
                        <Popover
                            align={{
                                offset: [14, 0],
                            }}
                            arrow={false}
                            content={
                                <div className="status-content">
                                    <Menu
                                        items={statusMenu}
                                        selectedKeys={[selectedKey]}
                                        onClick={changeStatus}
                                    />
                                </div>
                            }
                            placement="right"
                            trigger={["hover"]}
                        >
                            <div className="account-item">
                                <span className={`account-status ${"dot-" + selectedKey}`}></span>
                                <span className="account-status-text">
                                    {t("account.status." + selectedKey)}
                                </span>
                                <Icon
                                    component={RightArrow}
                                    onPointerMoveCapture={undefined}
                                    onPointerOverCapture={undefined}
                                />
                            </div>
                        </Popover>
                        <div
                            className="account-item"
                            onClick={() => handlePersonalSetting && handlePersonalSetting()}
                        >
                            {t("account.setting")}
                            <Icon
                                component={RightArrow}
                                onPointerMoveCapture={undefined}
                                onPointerOverCapture={undefined}
                            />
                        </div>
                    </div>
                    <div
                        className="account-fn"
                        onClick={() => handleQuitAccount && handleQuitAccount()}
                    >
                        {t("account.quit")}
                    </div>
                </Fragment>
            )}
        </div>
    );
};

const RightArrow = (): React.ReactElement => (
    <svg fill="none" height="14" viewBox="0 0 8 14" width="8" xmlns="http://www.w3.org/2000/svg">
        <path
            clipRule="evenodd"
            d="M7.80523 7.47518C8.06492 7.21252 8.06492 6.78816 7.80523 6.52482L1.64191 0.285008C1.26702 -0.0950026 0.657083 -0.0950026 0.281531 0.285008C-0.0933561 0.665018 -0.0933561 1.28177 0.281531 1.66178L5.55391 7.00034L0.281531 12.3375C-0.0933561 12.7182 -0.0933561 13.335 0.281531 13.715C0.657083 14.095 1.26702 14.095 1.64191 13.715L7.80523 7.47518Z"
            fill="#7E7E7E"
            fillRule="evenodd"
        />
    </svg>
);

const Logo = (): React.ReactElement => {
    return (
        <svg
            fill="none"
            height="58"
            version="1.1"
            viewBox="0 0 57 58"
            width="57"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
        >
            <g style={{ opacity: 0.10000000149011612 }}>
                <g>
                    <path
                        d="M12.217498,9.2288256L13.055937,3.2181644C13.340794,1.1760464,15.23071,-0.24869217,17.276701,0.036284275C19.323071,0.32131293,20.750719,2.2077546,20.465809,4.2502503L20.012817,7.4976869C22.790382,6.929882,25.675926,6.6447649,28.5,6.6447649C30.896002,6.6447649,33.31974,6.8582516,35.677784,7.2762017L35.255516,4.2508368C34.970432,2.2083645,36.397926,0.32180443,38.444271,0.036605202C40.490234,-0.24854112,42.380276,1.1760414,42.665306,3.2181361L43.504238,9.2287264C43.520145,9.3426809,43.53072,9.4561501,43.536152,9.5688829C45.850239,10.554215,47.946926,11.780401,49.696873,13.234947C54.854362,17.521818,57,24.148479,57,30.690025C57,37.231571,54.854362,44.036346,49.696873,48.501324L49.678799,48.516953L50.042809,56.853989C50.079613,57.69701,49.189331,58.264153,48.440804,57.874504L40.02964,53.496197C36.38229,54.568474,32.404316,55.091515,28.5,55.091515C20.629942,55.091515,12.282484,52.966316,7.125,48.501324C1.9675144,44.036346,0,37.231571,0,30.690025C0,24.148479,1.9675144,17.610874,7.125,13.234947C8.5859957,11.995348,10.302973,10.929215,12.192566,10.037742C12.171898,9.7732201,12.179334,9.5024233,12.217498,9.2288256ZM13.537476,31.045856C13.537476,22.683619,20.235561,15.90625,28.499973,15.90625C36.764389,15.90625,43.462471,22.683619,43.462471,31.045856C43.462471,39.408089,36.764389,46.185463,28.499973,46.185463C20.235561,46.185463,13.537476,39.408089,13.537476,31.045856ZM18.700157,31.045856C18.700157,36.519882,23.089981,40.96167,28.499973,40.96167C33.909973,40.96167,38.299789,36.519882,38.299789,31.045856C38.299789,25.571823,33.909973,21.130039,28.499973,21.130039C23.089981,21.130039,18.700157,25.571823,18.700157,31.045856ZM24.787764,30.759766L20.877563,30.759766C20.877563,35.174816,24.418877,38.758049,28.78228,38.758049L28.78228,34.801567C26.579498,34.801567,24.787764,32.988621,24.787764,30.759766Z"
                        fill="#FFFFFF"
                        fillOpacity="1"
                        fillRule="evenodd"
                    />
                </g>
            </g>
        </svg>
    );
};
